import Chevron from 'src/components/dashboard/components/navigationMenu/components/chevron';
import { formatDate } from 'src/utils/formatDate';
import { formatMoney } from 'src/utils/formatMoney';
import { useLocalization } from 'src/utils/useLocalization';
import {
    AccountType as AccountTypeEnum,
    PaymentReason,
} from 'src/utils/enums/payment.enum';
import { compareDates, compareNumbers } from 'src/utils/helpers';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import './style.scss';

export interface Iitems {
    PaymentTransactionId: string;
    AccountType: string;
    AccountNumber: string;
    Vin: string;
    PaymentAmount: string;
    SelectedPaymentReason: string;
    PaymentProcessStatus: string;
    ProcessDateTime: string;
}

export interface ITableContents {
    items: Iitems[];
    setDetail: (vin: string, transaction: string) => void;
}

export interface CreditStatusTableItemData {
    data: Iitems;
    setDetail: Dispatch<
        SetStateAction<{ applicationId: string; applicationNumber: number }>
    >;
}

export const TableItem: React.FC<any> = ({ data, setDetail }) => {
    const {
        PaymentTransactionId,
        AccountType,
        AccountNumber,
        Vin,
        PaymentAmount,
        SelectedPaymentReason,
        PaymentProcessStatus,
        ProcessDateTime,
    } = data;
    const {
        payments,
        paymentAccountEnumToUi,
        paymentStatusEnumToUi,
        paymentReasonEnumToUi,
    } = useLocalization();
    function handlerSetDetails() {
        if (setDetail) setDetail(AccountNumber, PaymentTransactionId);
    }
    return (
        <div className='row'>
            <div className='table-row col-1'>
                <span className='list-label'>
                    {payments.PAYMENT_INFORMATION_TABLE_HEADERS_TRANSACTION}
                </span>
                <span
                    className='underline'
                    onClick={() => {
                        handlerSetDetails();
                    }}
                >
                    {PaymentTransactionId}
                </span>
            </div>
            <div className='table-row col-2'>
                <span className='list-label'>
                    {payments.PAYMENT_INFORMATION_TABLE_HEADERS_TYPE}
                </span>
                <span>
                    {paymentAccountEnumToUi[AccountType as AccountTypeEnum]}
                </span>
            </div>
            <div className='table-row col-3'>
                <span className='list-label'>
                    {payments.PAYMENT_INFORMATION_TABLE_HEADERS_ACCOUNT}
                </span>
                <span className='dot-label'>{AccountNumber}</span>
            </div>
            <div className='table-row credit-table-col-4'>
                <span className='list-label'>
                    {payments.PAYMENT_INFORMATION_TABLE_HEADERS_VIN}
                </span>
                <span>{Vin}</span>
            </div>
            <div className='table-row col-5'>
                <span className='list-label'>
                    {payments.PAYMENT_INFORMATION_TABLE_HEADERS_AMOUNT}
                </span>
                <span>{formatMoney(PaymentAmount)}</span>
            </div>
            <div className='table-row col-6'>
                <span className='list-label'>
                    {payments.PAYMENT_INFORMATION_TABLE_HEADERS_METHOD}
                </span>
                <span>
                    {
                        paymentReasonEnumToUi[
                            SelectedPaymentReason as PaymentReason
                        ]
                    }
                </span>
            </div>
            <div className='table-row col-7'>
                <span className='list-label'>
                    {payments.PAYMENT_INFORMATION_TABLE_HEADERS_STATUS}
                </span>
                <span>{paymentStatusEnumToUi[PaymentProcessStatus]}</span>
            </div>
            <div className='table-row col-8'>
                <span className='list-label'>
                    {payments.PAYMENT_INFORMATION_TABLE_HEADERS_DATE}
                </span>
                <span>{formatDate(ProcessDateTime)}</span>
            </div>
        </div>
    );
};

export const Table: React.FC<ITableContents> = ({ items, setDetail }) => {
    const chevronColor = '#363b49';
    const { payments } = useLocalization();
    const [records, setRecords] = useState<Iitems[]>([]);
    const [orderType, setOrderType] = useState({ sort: '', or: '' });
    useEffect(() => {
        if (items.length) {
            sortItems('ProcessDateTime', true);
        }
    }, [items]);

    /**
     * Function to sort table based on property name and sort order
     * @param property
     * @param order
     */
    const sortItems = (property: keyof Iitems, order: boolean) => {
        let sorted: any = [...items];
        switch (property) {
            case 'AccountNumber':
            case 'PaymentAmount':
                setOrderType({ sort: property, or: !order ? 'asc' : 'desc' });
                sorted = compareNumbers(sorted, property, order);
                break;
            case 'ProcessDateTime':
                setOrderType({ sort: property, or: !order ? 'asc' : 'desc' });
                sorted = compareDates(sorted, property, !order);
                break;
            default:
                break;
        }
        setRecords(sorted);
    };

    /**
     * Function to render sorting buttons in table header
     * @param property
     * @param clickhandler
     * @param initialSort
     * @returns
     */
    const renderSorting = (
        property: string,
        clickhandler: any,
        initialSort = false,
    ) => {
        return (
            <span className='table-header__order'>
                <span
                    onClick={() => {
                        clickhandler(
                            property,
                            orderType.sort !== property
                                ? !initialSort
                                : orderType.or === 'asc'
                                ? !initialSort
                                : initialSort,
                        );
                    }}
                >
                    <Chevron
                        className={`accordion__icon ${
                            orderType.sort === property &&
                            orderType.or === 'desc'
                                ? 'rotate-down'
                                : 'rotate-up'
                        }`}
                        width={10}
                        fill={chevronColor}
                    />
                </span>
                {orderType.sort !== property && (
                    <span
                        onClick={() => {
                            clickhandler(property, initialSort);
                        }}
                    >
                        <Chevron
                            className='accordion__icon rotate-down'
                            width={10}
                            fill={chevronColor}
                        />
                    </span>
                )}
            </span>
        );
    };

    return (
        <div className='table-payment-pending'>
            <div className='row header-row hidden-in-list'>
                <div className='table-header col-1'>
                    <span className='u-text-truncate'>
                        {payments.PAYMENT_INFORMATION_TABLE_HEADERS_TRANSACTION}
                    </span>
                </div>
                <div className='table-header col-2'>
                    <span className='u-text-truncate'>
                        {payments.PAYMENT_INFORMATION_TABLE_HEADERS_TYPE}
                    </span>
                </div>
                <div className='table-header col-3'>
                    <span className='u-text-truncate'>
                        {payments.PAYMENT_INFORMATION_TABLE_HEADERS_ACCOUNT}
                    </span>
                    {renderSorting('AccountNumber', sortItems)}
                </div>
                <div className='table-header col-4'>
                    <span className='u-text-truncate'>
                        {payments.PAYMENT_INFORMATION_TABLE_HEADERS_VIN}
                    </span>
                </div>
                <div className='table-header col-5'>
                    <span className='u-text-truncate'>
                        {payments.PAYMENT_INFORMATION_TABLE_HEADERS_AMOUNT}
                    </span>
                    {renderSorting('PaymentAmount', sortItems)}
                </div>
                <div className='table-header col-6'>
                    <span className='u-text-truncate'>
                        {payments.PAYMENT_INFORMATION_TABLE_HEADERS_METHOD}
                    </span>
                </div>
                <div className='table-header col-7'>
                    <span className='u-text-truncate'>
                        {payments.PAYMENT_INFORMATION_TABLE_HEADERS_STATUS}
                    </span>
                </div>
                <div className='table-header col-8'>
                    <span className='u-text-truncate'>
                        {payments.PAYMENT_INFORMATION_TABLE_HEADERS_DATE}
                    </span>
                    {renderSorting('ProcessDateTime', sortItems, false)}
                </div>
            </div>
            {records.map((item, key) => (
                <TableItem data={item} key={key} setDetail={setDetail} />
            ))}
        </div>
    );
};

export default Table;
